import { useEffect, useState } from 'react';
import LeadModal from '@/pages/home/components/practitioner/LeadModal';
import ProspectForm from '@/pages/home/components/practitioner/ProspectForm';
import ContactForm from '@/pages/home/components/practitioner/ContactForm';

export type PractitionerAction = 'inscription' | 'boost' | 'seo';

type PractitionerLeadDialogProps = {
  action: PractitionerAction | null;
  onClose: () => void;
};

const boostOptions = [
  { value: 'Référencer mon entreprise', icon: 'ri-map-pin-2-line' },
  { value: 'Renforcer ma visibilité digitale', icon: 'ri-smartphone-line' },
  { value: 'Activer l\u2019agenda RDV en temps réel', icon: 'ri-calendar-check-line' },
  { value: 'Diffuser des campagnes publicitaires', icon: 'ri-megaphone-line' },
];

const seoPillars = [
  {
    tag: 'SEO',
    icon: 'ri-google-line',
    title: 'Référencement Google',
    desc: 'Votre cabinet remonte en tête des recherches locales « médecin + quartier », avec une fiche complète et optimisée.',
  },
  {
    tag: 'GEO',
    icon: 'ri-map-pin-line',
    title: 'Référencement local',
    desc: 'Vous apparaissez sur les requêtes géolocalisées des patients qui cherchent un soin près de chez eux.',
  },
  {
    tag: 'AEO',
    icon: 'ri-sparkling-2-line',
    title: 'Moteurs de réponse & IA',
    desc: 'ChatGPT, Perplexity et les moteurs de réponse citent votre cabinet comme source fiable.',
  },
];

export default function PractitionerLeadDialog({ action, onClose }: PractitionerLeadDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  const [selected, setSelected] = useState<string[]>([]);

  // Réinitialise le parcours à chaque ouverture.
  useEffect(() => {
    if (action) {
      setStep(1);
      setSelected([]);
    }
  }, [action]);

  const toggleOption = (value: string) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const open = action !== null;

  return (
    <LeadModal open={open} onClose={onClose}>
      {action === 'inscription' && (
        <>
          <div className="mb-6 pr-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-secondary-700 mb-2">
              Fiche gratuite · Statut standard
            </span>
            <h3 className="font-heading text-2xl font-semibold text-foreground-950 mb-2">
              Créer ma fiche gratuite
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Renseignez vos coordonnées : votre fiche prospect est créée immédiatement avec le
              statut standard. Aucun agenda, aucun engagement, aucune carte bancaire.
            </p>
          </div>
          <ProspectForm />
        </>
      )}

      {action === 'boost' && (
        <>
          <div className="mb-6 pr-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-2">
              {step === 1 ? 'Étape 1 / 2 · Vos besoins' : 'Étape 2 / 2 · Vos coordonnées'}
            </span>
            <h3 className="font-heading text-2xl font-semibold text-foreground-950 mb-2">
              {step === 1 ? 'Demander un devis' : 'Votre demande de devis'}
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              {step === 1
                ? 'Sélectionnez ce qui vous intéresse (plusieurs choix possibles). Nous vous préparons une proposition sur mesure.'
                : 'Dernière étape : complétez vos coordonnées pour recevoir votre devis personnalisé.'}
            </p>
          </div>

          {step === 1 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {boostOptions.map((option) => {
                  const isSelected = selected.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => toggleOption(option.value)}
                      className={`flex items-center gap-3 p-4 rounded-xl border text-left transition-colors cursor-pointer ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-background-200 bg-background-100 hover:border-primary-300'
                      }`}
                    >
                      <span className={`w-5 h-5 flex items-center justify-center rounded-md border flex-shrink-0 ${
                        isSelected ? 'bg-primary-500 border-primary-500' : 'bg-background-50 border-background-300'
                      }`}>
                        {isSelected && <i className="ri-check-line text-background-50 text-sm w-4 h-4 flex items-center justify-center" />}
                      </span>
                      <span className="flex items-center gap-2 min-w-0">
                        <i className={`${option.icon} text-lg w-5 h-5 flex items-center justify-center ${isSelected ? 'text-primary-600' : 'text-foreground-500'}`} />
                        <span className="text-sm font-medium text-foreground-900">{option.value}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {selected.length === 0 && (
                <p className="mt-3 text-xs text-foreground-500">
                  Sélectionnez au moins un besoin pour continuer.
                </p>
              )}

              <button
                type="button"
                disabled={selected.length === 0}
                onClick={() => setStep(2)}
                className="mt-5 w-full flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Continuer
                <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center" />
              </button>
            </>
          ) : (
            <>
              <div className="mb-5 p-4 rounded-xl bg-background-100 border border-background-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-2">
                  Besoins sélectionnés
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.map((item) => (
                    <span key={item} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-primary-900 text-xs font-medium">
                      <i className="ri-check-line text-sm w-3.5 h-3.5 flex items-center justify-center" />
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <ContactForm requestType="quote" preselectedOptions={selected.join(', ')} submitLabel="Envoyer ma demande de devis" />
            </>
          )}
        </>
      )}

      {action === 'seo' && (
        <>
          <div className="mb-6 pr-10">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent-700 mb-2">
              SEO · GEO · AEO
            </span>
            <h3 className="font-heading text-2xl font-semibold text-foreground-950 mb-2">
              Faites-vous connaître en ligne
            </h3>
            <p className="text-sm text-foreground-600 leading-relaxed">
              Aujourd'hui, un patient ne cherche plus seulement sur Google : il interroge aussi des
              assistants IA. Voici les trois leviers pour que votre cabinet soit trouvé partout.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {seoPillars.map((pillar) => (
              <div key={pillar.tag} className="rounded-xl bg-background-100 border border-background-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                    <i className={`${pillar.icon} text-base w-4 h-4 flex items-center justify-center`} />
                  </span>
                  <span className="text-xs font-bold uppercase tracking-wider text-accent-700">{pillar.tag}</span>
                </div>
                <p className="text-sm font-semibold text-foreground-950 mb-1">{pillar.title}</p>
                <p className="text-xs text-foreground-600 leading-relaxed">{pillar.desc}</p>
              </div>
            ))}
          </div>

          <div className="my-6 border-t border-background-200" />

          <p className="text-sm font-semibold text-foreground-950 mb-4">
            Demandez votre étude de visibilité gratuite
          </p>
          <ContactForm requestType="visibility-study" submitLabel="Découvrir mon potentiel en ligne" />
        </>
      )}
    </LeadModal>
  );
}